/**
 * A teacher on a session.
 *
 * The id is what anything grouping by teacher keys on. Names are not unique at
 * this studio — the workbook has two Ashleys — so a by-teacher view keyed on
 * the name merges them into one lane and hides a real double-booking (D-82).
 */
export type CalendarInstructor = {
  id: string
  name: string
  isSubstitute: boolean

  /** She helps with this class rather than teaching it (D-538). */
  isAssisting: boolean
}

/**
 * One practice video on a class (D-541): the teacher's label for which part,
 * and either an unlisted YouTube address or a file she uploaded (D-668).
 * The same shape everywhere it appears.
 *
 * **Exactly one of `url` and `mediaAssetId` is set**, which the server
 * enforces; the reader branches on `mediaAssetId` because that is the one
 * that changes how it is played — a link opens, a file streams through the
 * app at `/api/classes/{classId}/choreo/{mediaAssetId}`.
 *
 * Four modules held their own copy of this before the file half arrived, and
 * every one of them silently dropped `mediaAssetId` — so an editor that
 * round-tripped the list deleted somebody's upload. One type, imported.
 */
export type ChoreoLink = {
  label: string | null
  url: string | null
  mediaAssetId: string | null
}

/** Which way a session belongs to the person reading it (D-539). */
export type MyPart = 'teaching' | 'assisting' | 'dancing'

/**
 * How this session is hers, or `null` when saying so would be noise.
 *
 * **Only for somebody whose week genuinely holds both kinds** — a dancer here
 * who is also on the staff. Ava dances five classes and assists one, and they
 * arrive in one week because that is the week she needs: she can dance at 7:30
 * and assist at 7:30, and two tabs would hide the clash rather than show it.
 *
 * **Everybody else gets `null` and no badge.** A teacher's week is all
 * teaching and a dancer's is all dancing, so a label on every row would be a
 * word repeated twelve times that answers nothing.
 *
 * **`isAssisting` comes from the server, and is not derived from "her id is in
 * the list".** That derivation would print *teaching* over a class she may not
 * even take the register for (D-538), on the screen built to tell her apart.
 */
export function myPartIn(
  entry: { instructors: CalendarInstructor[] },
  me: { staffId?: string | null; isDancer?: boolean },
  /**
   * The week being read is the one that holds both halves of her — her own
   * scope, with `teachingOnly` off and the studio view off.
   *
   * **Passed in, because the reader alone cannot answer it.** An audit found
   * the first version keyed on *who she is*: with "Show studio schedule" on,
   * every one of opening night's 127 sessions read **Dancing**, and a
   * teacher-parent-dancer's narrowed tab read **Teaching** twelve times over.
   * Inside her own mixed week "not an instructor" is not a guess — the scope
   * is exactly *her assignments ∪ her enrolments*, so absence from the first
   * means presence in the second. Outside it the same inference is a lie.
   */
  weekMixes: boolean,
): MyPart | null {
  if (!weekMixes || me.isDancer !== true || !me.staffId) return null

  const mine = entry.instructors.find((i) => i.id === me.staffId)

  if (!mine) return 'dancing'
  return mine.isAssisting ? 'assisting' : 'teaching'
}

/**
 * Whether the roster behind this session is hers to open (D-539).
 *
 * **The card must not offer what the server will refuse** — the project's own
 * rule, and an audit found it broken twice on the one screen a dancer has: a
 * plain dancer holds `ViewSchedule` only and `/api/rosters` wants
 * `ViewPeople`, so her own Tuesday ballet answered with a red permission box;
 * Ava holds `ViewPeople` but `MayReadRosterAsync` narrows her to classes she is
 * assigned to, so the five she *dances* answered `Roster request failed (404)`.
 *
 * **The same two facts the server uses, nothing more.** No `ViewPeople` means
 * no roster, for anyone. A dancer on the staff sees only the rosters of classes
 * she is on (D-538's rule, restated). Everybody else is unchanged — an ordinary
 * teacher, an admin and Tonya keep every card they had.
 */
export function canOpenRoster(
  /** Ids rather than entries, so the week card and the day block share one rule. */
  instructorIds: string[],
  me: { staffId?: string | null; isDancer?: boolean; capabilities?: string[] },
): boolean {
  if (!(me.capabilities ?? []).includes('ViewPeople')) return false

  if (me.isDancer === true && me.staffId) {
    return instructorIds.includes(me.staffId)
  }

  return true
}

/** Comma-separated names, for anywhere a person just needs to read them. */
export function instructorNames(instructors: CalendarInstructor[]): string {
  return instructors.map((i) => i.name).join(', ')
}

/**
 * Somebody is covering this one (D-365).
 *
 * **`isSubstitute` arrived with the payload and was rendered nowhere** — a
 * whole field, on every calendar entry, that no screen read. It did not matter
 * while `CoverPanel` listed what a teacher was covering below her week; it
 * matters the moment that list goes, because otherwise the fact has no home at
 * all. An earlier note claiming her week already said so was wrong: the
 * sentence lived only on the home session card, capped at five.
 */
export function isCovered(instructors: CalendarInstructor[]): boolean {
  return instructors.some((i) => i.isSubstitute)
}

export type CalendarEntry = {
  occurrenceId: string
  classId: string
  className: string
  startTime: string | null
  endTime: string | null
  room: string | null
  roomId: string | null
  roomColor: string | null
  instructors: CalendarInstructor[]
  program: string
  style: string | null
  isCancelled: boolean
  cancellationReason: string | null
  enrolled: number
  capacity: number | null
  /** The class's practice videos for families (D-541), in order; empty when none. */
  choreoLinks?: ChoreoLink[]
  /** The class's practice music, for a dancer's own week as well (D-596). */
  musicUrl?: string | null
  /**
   * The booking a private lesson's session belongs to (D-733). A schedule that
   * also draws that booking shows the lesson as the booking's card only - see
   * `withoutShownPrivates`.
   */
  bookingId?: string | null
}

export type CalendarDay = {
  date: string
  entries: CalendarEntry[]
}

export type CalendarWeek = {
  weekStart: string
  weekEnd: string
  days: CalendarDay[]
  /** Sessions after filtering. */
  totalEntries: number
  /** Sessions before filtering, so the UI can say what's hidden. */
  unfilteredTotal: number
  hiddenByFilter: number
  /** Only present when the week is genuinely empty (D-100). */
  nextSessionDate: string | null
  previousSessionDate: string | null
}

/**
 * Why a week is empty, and what to offer about it.
 *
 * The season runs September to June, so most of the calendar year is
 * legitimately blank — and a blank grid in August reads as lost data unless it
 * says otherwise. Filters are checked first: a week emptied by a filter is
 * already explained, and pointing that reader at September would answer a
 * question they did not ask.
 */
export type EmptyWeek =
  | { kind: 'filtered'; hidden: number }
  | { kind: 'before-season'; jumpTo: string }
  | { kind: 'after-season'; jumpTo: string }
  | { kind: 'break'; jumpTo: string }
  | { kind: 'nothing-scheduled' }

/**
 * Whether an empty-week notice should still be shown once bookings are known
 * (D-372).
 *
 * **A pure decision, because the rendered version could not be tested.** The
 * guard lived inline in the week view, and a test of it passed at `t=0` — with
 * the bookings query unresolved the count is zero, the notice renders, and the
 * assertion succeeds before the data that would change it has arrived. Removing
 * the cancelled filter left it green. There is nothing positive on screen to
 * wait for when every booking is filtered out, so the decision moved here where
 * it can be asked directly.
 *
 * **`filtered` is the exception.** *"5 sessions are scheduled this week — clear
 * a filter"* stays true with a private on screen, and it is the sentence that
 * hands the reader the way out. Every other kind says "there is nothing here",
 * which a visible card contradicts.
 */
export function shouldExplainEmptyWeek(reason: EmptyWeek | null, liveBookings: number): boolean {
  if (!reason) return false
  if (reason.kind === 'filtered') return true

  return liveBookings === 0
}

export function explainEmptyWeek(week: CalendarWeek): EmptyWeek | null {
  if (week.totalEntries > 0) return null

  if (week.hiddenByFilter > 0) return { kind: 'filtered', hidden: week.hiddenByFilter }

  const { nextSessionDate: next, previousSessionDate: previous } = week

  // Classes on both sides means a gap inside the season — a holiday week or
  // spring break, not a scheduling mistake.
  if (next && previous) return { kind: 'break', jumpTo: next }
  if (next) return { kind: 'before-season', jumpTo: next }
  if (previous) return { kind: 'after-season', jumpTo: previous }

  return { kind: 'nothing-scheduled' }
}

export type CalendarFilter = {
  program?: string
  room?: string
  instructor?: string
  style?: string
  q?: string
  hideCancelled?: boolean
  /**
   * Show only the privates this teacher is down for (D-649).
   *
   * <b>Client-side, and it has to be.</b> The calendar service knows nothing
   * about bookings at all - classes come from the week request and privates are
   * overlaid from a second one (D-372) - so a server-side filter would have to
   * teach one of them about the other. The data is already labelled on both
   * sides, so the filter is a predicate over what is on screen.
   *
   * Hides classes as well as other kinds of booking. A teacher asking *what
   * privates am I down for* is not asking for her Tuesday ballet to be kept
   * alongside them.
   */
  privatesOnly?: boolean
  /**
   * A staff member asking for the whole studio rather than her own classes
   * (D-299).
   *
   * It travels with the filter because every calendar read already takes one —
   * so the week, the day, the month and the filter options all widen together
   * rather than three of them agreeing and one not.
   *
   * Ignored by the server for anybody who is not staff.
   */
  wholeStudio?: boolean

  /**
   * One dancer's evening: the classes she is in, and nothing else (D-606).
   *
   * Set by the conflicts screen rather than by a picker — the calendar already
   * carries four filters, and a fifth listing three hundred dancers would be a
   * control nobody opens.
   *
   * **Honoured only for somebody who may already see every schedule.** The
   * calendar sits behind `ViewSchedule`, which every role holds, so an ungated
   * dancer parameter would let any parent ask which classes another family's
   * child is in. The server ignores it otherwise.
   */
  dancer?: string

  /** Her name, so the chip can say whose evening this is rather than an id. */
  dancerName?: string

  /**
   * The teaching half only — what she instructs, without her own children's
   * classes (D-374).
   *
   * For the sixteen accounts that are both. Her scope is a union, which is
   * right for home; *My classes* asks a narrower question, and the union
   * answered it by putting her daughter's ballet on the tab carrying her own
   * name.
   *
   * **On the filter rather than beside it**, so the react-query key changes
   * with it. Held outside, switching tabs would serve the other half's cached
   * week and the fix would appear not to work.
   */
  teachingOnly?: boolean
}

export type CalendarFilterOptions = {
  programs: string[]
  rooms: string[]
  instructors: string[]
  styles: string[]
}

export function filterIsActive(f: CalendarFilter): boolean {
  return Boolean(f.program || f.room || f.instructor || f.style || f.q?.trim() || f.hideCancelled || f.privatesOnly)
}

/*
 * **One serialiser, because there were two and they drifted** (D-368).
 *
 * This built the week's query and emitted `wholeStudio`; `appendFilter` built
 * the day's and the month's and did not — while the type's own comment three
 * screens up promised that "the week, the day, the month and the filter options
 * all widen together rather than three of them agreeing and one not". They did
 * not, and the day is what the booking form's room grid reads: a teacher saw
 * only her own classes in a room, so an occupied room reported free.
 *
 * Composed rather than copied, so the next field added cannot reach one caller
 * and miss the other two.
 */
function toQuery(date: string, filter: CalendarFilter): string {
  const params = new URLSearchParams({ date })
  appendFilter(params, filter)
  return params.toString()
}

export async function fetchWeek(date: string, filter: CalendarFilter = {}): Promise<CalendarWeek> {
  const res = await fetch(`/api/calendar/week?${toQuery(date, filter)}`)
  if (!res.ok) throw new Error(`Calendar request failed (${res.status})`)
  return res.json()
}

/**
 * The values the filter controls should offer.
 *
 * **Takes the same view flags the grid does** (D-299, D-374). Without them the
 * dropdowns describe a different calendar from the one beside them: on *My
 * classes* they would list her children's rooms and their teachers, offering
 * filters that can only empty the grid — and on the studio view they would be
 * narrower than what is on screen.
 */
export async function fetchFilterOptions(
  date: string,
  view: Pick<CalendarFilter, 'wholeStudio' | 'teachingOnly'> = {},
): Promise<CalendarFilterOptions> {
  const params = new URLSearchParams({ date })
  if (view.wholeStudio) params.set('wholeStudio', 'true')
  if (view.teachingOnly) params.set('teachingOnly', 'true')

  const res = await fetch(`/api/calendar/filters?${params}`)
  if (!res.ok) throw new Error(`Filter options request failed (${res.status})`)
  return res.json()
}

export type MonthPreview = {
  className: string
  roomColor: string | null
}

export type MonthDay = {
  date: string
  classCount: number
  cancelledCount: number
  isClosed: boolean
  closureReason: string | null
  /** Shut from this time onward; null with isClosed means all day (D-741). */
  closedFrom: string | null
  /**
   * The closure cancelled only the regularly scheduled classes (D-868), so
   * sessions on this day may still be running.
   *
   * A festival closure *is* a whole-day closure, so without this the cell takes
   * the nothing-runs branch and the one-off class and the private that are
   * still on vanish from the month — the same way the 4:30 classes did before
   * `closedFrom` existed.
   */
  regularScheduleOnly: boolean
  isInMonth: boolean
  preview: MonthPreview[]
}

export type CalendarMonth = {
  year: number
  month: number
  monthName: string
  gridStart: string
  gridEnd: string
  days: MonthDay[]
  totalClasses: number
}

export type DayRoom = {
  roomId: string
  name: string
  color: string | null
  entries: CalendarEntry[]
}

export type CalendarDayDetail = {
  date: string
  entries: CalendarEntry[]
  isClosed: boolean
  closureReason: string | null
  /** Shut from this time onward; null with isClosed means all day (D-741). */
  closedFrom: string | null
  /**
   * The closure cancelled only the regularly scheduled classes (D-868).
   *
   * ⚠ The day was never given this, so its banner read *"Studio closed."* on a
   * festival day while the privates and one-off classes beneath it were going
   * ahead. The month cell gained the field when the scope landed; this did not,
   * because nothing points one DTO at the other (D-870).
   */
  regularScheduleOnly: boolean
  rooms: DayRoom[]
  gridStart: string
  gridEnd: string
  slotMinutes: number
  slotCount: number
  unscheduled: CalendarEntry[]
  /** How wide the room may be booked, far beyond class hours (D-79). */
  bookingWindowStart: string
  bookingWindowEnd: string
}

export async function fetchMonth(
  year: number,
  month: number,
  filter: CalendarFilter = {},
): Promise<CalendarMonth> {
  const params = new URLSearchParams({ year: String(year), month: String(month) })
  appendFilter(params, filter)
  const res = await fetch(`/api/calendar/month?${params}`)
  if (!res.ok) throw new Error(`Month request failed (${res.status})`)
  return res.json()
}

export async function fetchDay(date: string, filter: CalendarFilter = {}): Promise<CalendarDayDetail> {
  const params = new URLSearchParams({ date })
  appendFilter(params, filter)
  const res = await fetch(`/api/calendar/day?${params}`)
  if (!res.ok) throw new Error(`Day request failed (${res.status})`)
  return res.json()
}

function appendFilter(params: URLSearchParams, filter: CalendarFilter) {
  if (filter.program) params.set('program', filter.program)
  if (filter.room) params.set('room', filter.room)
  if (filter.instructor) params.set('instructor', filter.instructor)
  if (filter.style) params.set('style', filter.style)
  if (filter.q?.trim()) params.set('q', filter.q.trim())
  if (filter.hideCancelled) params.set('hideCancelled', 'true')
  if (filter.wholeStudio) params.set('wholeStudio', 'true')
  if (filter.teachingOnly) params.set('teachingOnly', 'true')

  // Her name never goes to the server: it is on the URL so the chip can say
  // whose evening this is, and the server answers on the id alone (D-606).
  if (filter.dancer) params.set('dancer', filter.dancer)
}

/** "17:15:00" -> minutes since midnight. */
export function toMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number)
  return h * 60 + m
}

/**
 * Where the finish goes when the start moves and the session keeps its length.
 *
 * Shared rather than written twice (D-700): the counter-offer on an ask and the
 * move panel on a roster both do this, and a form where dragging the start
 * silently shortens the class is the same small wrongness in both. Returns null
 * when it has nothing to say — an unparseable field, a finish already at or
 * before the start, or a shift that would run past midnight — and the caller
 * leaves the finish where the person put it.
 */
export function shiftEnd(start: string, end: string, nextStart: string): string | null {
  /*
   * `toMinutes` three lines up, not a second parser beside it — a private copy
   * here would be two implementations of one idea inside one file, in the
   * change whose whole point is removing exactly that from two components.
   * It returns NaN for a half-typed field, so the guard is a finiteness check.
   */
  const from = toMinutes(start)
  const to = toMinutes(nextStart)
  const finish = toMinutes(end)

  if (![from, to, finish].every(Number.isFinite)) return null

  const length = finish - from
  if (length <= 0) return null

  const shifted = to + length
  if (shifted >= 24 * 60) return null

  return `${String(Math.floor(shifted / 60)).padStart(2, '0')}:${String(shifted % 60).padStart(2, '0')}`
}

export function addMonths(year: number, month: number, delta: number): { year: number; month: number } {
  const zero = year * 12 + (month - 1) + delta
  return { year: Math.floor(zero / 12), month: (zero % 12) + 1 }
}

export function formatFullDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

/** "17:15:00" -> "5:15" */
export function formatTime(time: string | null): string | null {
  if (!time) return null
  const [h, m] = time.split(':').map(Number)
  const hour = h % 12 === 0 ? 12 : h % 12
  return `${hour}:${String(m).padStart(2, '0')}`
}

export function meridiem(time: string | null): string {
  if (!time) return ''
  return Number(time.split(':')[0]) >= 12 ? 'pm' : 'am'
}

/**
 * How a closure names its cutoff, for anything already saying "closed" (D-741).
 *
 * One function because the day banner and the month cell both ask it, and a
 * whole-day closure has to keep reading "Closed" rather than "Closed from
 * 12:00am" - two spellings of that distinction drift, and the drift tells a
 * teacher her 4:30 is off when it is not.
 */
export function closedFromSuffix(closedFrom: string | null): string {
  if (!closedFrom) return ''
  return ` from ${formatTime(closedFrom)}${meridiem(closedFrom)}`
}

/**
 * What to call a closed day, for the signed-in screens (D-868, D-870).
 *
 * **The scope branch lived in two components and only one of them had it.** The
 * month cell gained it the day `regularScheduleOnly` landed; the day banner was
 * never even given the field, so it said *"Studio closed"* over a list of
 * privates that were going ahead. Two copies of one sentence is how the second
 * one fails to receive the first one's fix.
 *
 * **The server composes its own** for the calendar feed (`ClosedDay.Sentence`),
 * which is correct rather than duplicated: the feed is read by a family who
 * never signs in, and these screens are read by somebody who did. What must not
 * differ is *which of the two states it is*, and that is decided here.
 *
 * @param closedWord what a full closure is called on this surface - the month
 * cell has room for `Closed` and the day banner for `Studio closed`.
 */
export function closureHeadline(
  closedFrom: string | null,
  regularScheduleOnly: boolean,
  closedWord = 'Studio closed',
): string {
  const noun = regularScheduleOnly ? 'Regular classes off' : closedWord
  return `${noun}${closedFromSuffix(closedFrom)}`
}

/*
 * Anchored to UTC deliberately. These are calendar dates, not instants: parsing
 * as local midnight and formatting back through toISOString() shifts the answer
 * by a day for any positive UTC offset, and again across a DST boundary.
 * Fixing the clock to UTC makes the arithmetic pure day-counting.
 */
export function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

export function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`
}

/*
 * Read from the local clock, never toISOString(). Arkansas is UTC-5/-6, so the
 * UTC date rolls over at 7pm — inside studio hours (Mon-Thu 4:30-8:30 PM), which
 * made "Today" land on tomorrow for the whole second half of every class night.
 */
/**
 * The date the server is simulating, when it is simulating one (D-386).
 *
 * **One clock for the client, because two of them disagreed in public.** The
 * override moves the *server's* date, so with it set the banner read "Showing
 * September 8" while the calendar opened on the real week — every screen driven
 * by a payload said one thing and every screen doing its own date maths said
 * another. `observed` on production, immediately after switching it on.
 *
 * A module-level value rather than a parameter because `today()` has **41 call
 * sites** and threading a date through all of them would be a worse change than
 * the bug. It is written from exactly one place, `useSession`, as the session
 * arrives, and it is null on every ordinary host.
 */
let simulatedToday: string | null = null

/**
 * Set from the session, and nowhere else.
 *
 * Passing null restores the real clock, which is what a signed-out or ordinary
 * session does — so this cannot leak a simulated date into a later session.
 */
export function setSimulatedToday(date: string | null): void {
  simulatedToday = date
}

export function today(): string {
  return simulatedToday ?? toIsoDate(new Date())
}

/** Monday-first, matching the week grid and the month view's column order. */
export function startOfWeek(iso: string): string {
  const dow = (new Date(`${iso}T00:00:00Z`).getUTCDay() + 6) % 7
  return addDays(iso, -dow)
}

/**
 * The 42 dates a month grid shows, including the leading and trailing days that
 * belong to the neighbouring months. Always six rows, so the picker never
 * changes height as you page through months.
 */
export function monthGrid(year: number, month: number): string[] {
  const start = startOfWeek(`${year}-${String(month).padStart(2, '0')}-01`)
  return Array.from({ length: 42 }, (_, i) => addDays(start, i))
}

export function monthOf(iso: string): { year: number; month: number } {
  return { year: Number(iso.slice(0, 4)), month: Number(iso.slice(5, 7)) }
}

export function formatDayLabel(iso: string): { weekday: string; day: string; month: string } {
  const d = new Date(`${iso}T00:00:00`)
  return {
    weekday: d.toLocaleDateString('en-US', { weekday: 'short' }),
    day: String(d.getDate()),
    month: d.toLocaleDateString('en-US', { month: 'short' }),
  }
}

/**
 * "August 3-9, 2026" / "August 31 - September 6, 2026" /
 * "December 28, 2026 - January 3, 2027" (dashes are en dashes at runtime).
 *
 * Built by hand rather than through Intl options. Asking toLocaleDateString for
 * day and year while omitting month does not drop the month, it renders
 * "2026 (day: 9)" - so the parts are assembled explicitly instead. (D-67)
 */
export function formatRange(start: string, end: string): string {
  const s = new Date(`${start}T00:00:00`)
  const e = new Date(`${end}T00:00:00`)

  const month = (d: Date) => d.toLocaleDateString('en-US', { month: 'long' })

  // Written as escapes, not literal glyphs: this file has been silently
  // re-encoded to ANSI more than once, and an en dash is the first casualty.
  const EN = '\u2013'

  if (s.getFullYear() !== e.getFullYear()) {
    return `${month(s)} ${s.getDate()}, ${s.getFullYear()} ${EN} ${month(e)} ${e.getDate()}, ${e.getFullYear()}`
  }

  if (s.getMonth() !== e.getMonth()) {
    return `${month(s)} ${s.getDate()} ${EN} ${month(e)} ${e.getDate()}, ${e.getFullYear()}`
  }

  // Tight dash between bare numerals, spaced when either side carries a word.
  // Matches Outlook ("August 10-14, 2026") and ordinary typographic practice.
  return `${month(s)} ${s.getDate()}${EN}${e.getDate()}, ${e.getFullYear()}`
}

export function formatMonthYear(year: number, month: number): string {
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}
